import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, BreadcrumbGroup, Button, Container, Grid, Input, SpaceBetween, StatusIndicator } from "@amzn/awsui-components-react";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { convertToLocalTime } from "./Utility";
import { useSelector } from "react-redux";
import './styles.scss';
import { getTickNTieHistory, batchGetTickNTieStatus, getDocumentTreeWithTracks } from "./DocumentStoreAPIs";
import DownloadTieOutResultsButton from "./DownloadTieOutResultsButton";
import TieOutResultContent from './TieOutResultContent';

const TieOutHistoryComponent = ({ quarter, associationId, associationType, onBack }) => {
    const gridRef = useRef();
    const [historyList, setHistoryList] = useState([]);
    const [quickFilterText, setQuickFilterText] = useState('');
    const [viewResultsDetails, setViewResultsDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const userId = useSelector((globalState) => globalState.auth.userId);

    const defaultColDef = {
        filter: true,
        filterParams: {
            buttons: ['reset'],
            debounceMs: 200
        }
    };

    const columnDefs = useMemo(() => [
        { field: 'section', headerName: 'Section', flex: 2, sortable: true, resizable: true },
        { field: 'version', headerName: 'Version', width: 120, sortable: true, resizable: true },
        { field: 'quarter', headerName: 'Quarter', width: 100, sortable: true, resizable: true },
        { field: 'year', headerName: 'Year', width: 80, sortable: true, resizable: true },
        { field: 'status', headerName: 'Status', width: 180, sortable: true, resizable: true },
        {
            field: 'results',
            headerName: 'Results',
            width: 200,
            sortable: false,
            filter: false,
            cellRenderer: (params) => {
                return (
                    <SpaceBetween direction="horizontal" size="s">
                        <Button onClick={() => fetchDetails(params?.data)} variant="inline-link">View</Button>
                        <DownloadTieOutResultsButton data={params?.data} />
                    </SpaceBetween>
                );
            }
        },
        {
            field: 'timestamp',
            headerName: 'Execution Date (Local Time)',
            width: 240,
            sortable: true,
            resizable: true,
            cellRenderer: (params) => { return params.value || ''; },
            valueGetter: (params) => {
                return params?.data?.timestamp ? convertToLocalTime(params.data.timestamp) : '';
            }
        },
        { field: 'request_id', headerName: 'ID', width: 150, resizable: true, sortable: true },
    ], []);

    useEffect(() => {
        if (quarter) {
            fetchHistory();
        }
    }, [quarter]);

    useEffect(() => {
        onFilterTextBoxChanged();
    }, [quickFilterText]);

    function onFilterTextBoxChanged() {
        gridRef?.current?.api?.setGridOption(
            "quickFilterText",
            quickFilterText,
        );
    }

    const fetchDetails = (data) => {
        setViewResultsDetails(data);
    };

    const fetchHistory = async () => {
        setIsLoading(true);
        try {
            // Build project identifier from quarter prop (e.g. "Q2 2026" -> "SEC_CENTRAL#Q2#2026")
            const [q, year] = (quarter || '').split(' ');
            const project = `SEC_CENTRAL#${q}#${year}`;

            // Fetch all history pages
            let allItems = [];
            let nextToken = null;
            do {
                const result = await getTickNTieHistory(project, 100, nextToken);
                const items = result?.items || result || [];
                allItems = [...allItems, ...(Array.isArray(items) ? items : [])];
                nextToken = result?.nextToken || null;
            } while (nextToken);

            // Fetch document tree to build block_id -> block_name lookup
            let blockNameMap = {};
            if (associationId) {
                try {
                    // Fetch both 10Q and 10K document trees to cover all blocks
                    const [docTree10Q, docTree10K] = await Promise.all([
                        getDocumentTreeWithTracks({ quarter, associationId, associationType, documentName: '10Q' }).catch(() => []),
                        getDocumentTreeWithTracks({ quarter, associationId, associationType, documentName: '10K' }).catch(() => []),
                    ]);
                    const allBlocks = [...(Array.isArray(docTree10Q) ? docTree10Q : []), ...(Array.isArray(docTree10K) ? docTree10K : [])];
                    allBlocks.forEach(row => {
                        const blockId = row.association_id || row.block_id || '';
                        const blockName = row.title || row.block_name || '';
                        if (blockId && blockName) {
                            blockNameMap[blockId] = blockName;
                        }
                    });
                } catch (err) {
                    console.error('Failed to fetch document tree for block names', err);
                }
            }

            // Map items to display format
            // unique_id format for history records: history#<block_id>#<wb_type>#<ISO datetime>
            const displayItems = allItems.map(item => {
                const uniqueId = item.unique_id || '';
                const parts = uniqueId.split('#');
                // parts[0] = "history", parts[1] = block_id, parts[2] = wb_type, parts[3] = ISO datetime
                const blockId = item.block_id || parts[1] || '';
                const wbType = item.wb_version || parts[2] || '';
                const itemTimestamp = item.timestamp || parts[3] || '';
                const version = wbType === 'PRE_WB' ? 'Pre-WB' : wbType === 'POST_WB' ? 'Post-WB' : wbType;

                // Use block_name from document tree lookup, fall back to blockId
                const sectionName = blockNameMap[blockId] || blockId;

                return {
                    ...item,
                    section: sectionName,
                    version,
                    block_id: blockId,
                    quarter: item.quarter || q || '',
                    year: item.year || year || '',
                    status: item.status || 'Completed',
                    timestamp: itemTimestamp,
                    request_id: item.request_id || '',
                };
            });

            // Sort by timestamp descending (most recent first)
            displayItems.sort((a, b) => {
                return (b.timestamp || '').localeCompare(a.timestamp || '');
            });

            // Fetch status data (contains annotated_pdf_s3_path, response_s3_path) for items with request_id + section_id
            const keysForStatus = displayItems
                .filter(item => item.request_id && item.section_id)
                .map(item => ({ request_id: item.request_id, section_id: item.section_id }));

            let statusMap = {};
            if (keysForStatus.length > 0) {
                try {
                    const statusResults = await batchGetTickNTieStatus({ keys: keysForStatus });
                    const statusItems = Array.isArray(statusResults) ? statusResults : [];
                    statusItems.forEach(statusItem => {
                        if (statusItem?.request_id) {
                            statusMap[statusItem.request_id] = statusItem;
                        }
                    });
                } catch (err) {
                    console.error('Failed to fetch status data for history items', err);
                }
            }

            // Merge status data into display items
            const enrichedItems = displayItems.map(item => {
                const statusData = statusMap[item.request_id] || {};
                return {
                    ...item,
                    ...statusData,
                    // Preserve our display fields over statusData
                    section: item.section,
                    version: item.version,
                    block_id: item.block_id,
                    quarter: item.quarter,
                    year: item.year,
                    status: item.status,
                    timestamp: item.timestamp,
                    request_id: item.request_id,
                };
            });

            setHistoryList(enrichedItems);
        } catch (e) {
            console.error('Failed to fetch tie-out history', e);
            setHistoryList([]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <SpaceBetween direction="vertical" size="m">
            <BreadcrumbGroup
                items={[
                    { text: "Tie-Out Dashboard", href: "#" },
                    { text: "History", href: "#" },
                ]}
                ariaLabel="Breadcrumbs"
                onFollow={(event) => {
                    event.preventDefault();
                    if (event.detail.text === "Tie-Out Dashboard" && onBack) {
                        onBack();
                    }
                }}
            />

            <Container>
                <SpaceBetween direction="vertical" size="m">
                    <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
                        <Input
                            onChange={({ detail }) => setQuickFilterText(detail.value)}
                            value={quickFilterText}
                            expandToViewport={true}
                            placeholder='Type to search'
                        />
                    </Grid>

                    <div style={{ width: '100%', height: '50vh' }}>
                        <div
                            className="ag-theme-alpine"
                            style={{ width: '100%', height: '100%', minHeight: '200px' }}
                        >
                            <AgGridReact
                                ref={gridRef}
                                rowData={historyList}
                                defaultColDef={defaultColDef}
                                columnDefs={columnDefs}
                                rowSelection="single"
                                animateRows={true}
                                pagination={true}
                                paginationPageSize={10}
                                paginationPageSizeSelector={[10, 50, 100]}
                                loading={isLoading}
                            />
                        </div>
                    </div>
                </SpaceBetween>
            </Container>

            {viewResultsDetails && (
                <Container>
                    <SpaceBetween direction="vertical" size="m">
                        <Box variant="h3">Tie-Out Result: {viewResultsDetails.section}</Box>
                        <Box>
                            <SpaceBetween direction="horizontal" size="l">
                                <Box><strong>Version:</strong> {viewResultsDetails.version}</Box>
                                <Box><strong>Quarter:</strong> {viewResultsDetails.quarter} {viewResultsDetails.year}</Box>
                                <Box><strong>Status:</strong> {viewResultsDetails.status}</Box>
                            </SpaceBetween>
                        </Box>
                        <TieOutResultContent data={viewResultsDetails} />
                    </SpaceBetween>
                </Container>
            )}
        </SpaceBetween>
    );
};

export default TieOutHistoryComponent;
